import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const services = [
    { name: 'Cybersecurity', path: '/services/cybersecurity' },
    { name: 'System Integration', path: '/services/system-integration' },
    { name: 'Network Services', path: '/services/network-services' },
    { name: 'AI & Intelligent Systems', path: '/services/ai-intelligent-systems' },
    { name: 'Business Automation & RPA', path: '/services/automation-rpa' },
    { name: 'Data Platforms & Intelligence', path: '/services/data-platforms' },
    { name: 'IoT & Smart Infrastructure', path: '/services/iot-smart-infra' },
    { name: 'Cloud, DevOps & Infrastructure', path: '/services/cloud-devops' },
    { name: 'Telecom Tower & RF Infrastructure', path: '/services/telecom-tower-rf-infrastructure' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src="/logo.png" 
              alt="Sanghvi Infotech" 
              className="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-110"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                location.pathname === '/' ? 'text-blue-600' : ''
              }`}
            >
              Home
            </Link>
            
            <Link
              to="/about"
              className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                location.pathname === '/about' ? 'text-blue-600' : ''
              }`}
            >
              About
            </Link>

            {/* Services Dropdown */}
            <div className="relative group">
              <button
                className={`text-gray-700 hover:text-blue-600 font-medium transition-colors flex items-center ${
                  location.pathname.startsWith('/services') ? 'text-blue-600' : ''
                }`}
              >
                Services
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-2">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/blog"
              className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                location.pathname.startsWith('/blog') ? 'text-blue-600' : ''
              }`}
            >
              Blog
            </Link>

            <Link to="/contact">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Contact Us
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-blue-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                  location.pathname === '/' ? 'text-blue-600' : ''
                }`}
              >
                Home
              </Link>
              
              <Link
                to="/about"
                className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                  location.pathname === '/about' ? 'text-blue-600' : ''
                }`}
              >
                About
              </Link>

              {/* Mobile Services */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="flex items-center justify-between w-full text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Services
                  <ChevronDown className={`h-4 w-4 transition-transform ${
                    isServicesOpen ? 'rotate-180' : ''
                  }`} />
                </button>
                {isServicesOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {services.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        className="block text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/blog"
                className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                  location.pathname.startsWith('/blog') ? 'text-blue-600' : ''
                }`}
              >
                Blog
              </Link>

              <Link to="/contact" className="pt-2">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Contact Us
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;